import { login } from '../api/auth'
import { ApiError } from '../api/client'
import { FORGOT_PASSWORD_URL } from '../config'
import { escapeHtml } from './dom'

/**
 * Login screen (PRD P0.5). Failure copy is deliberately generic — no account
 * enumeration. No credential is persisted anywhere; the session lives in the
 * HttpOnly cookie set by the proxy.
 *
 * This screen carried a "Continue without a password" link to the Q2-option-B
 * SSO hop (`/api/auth/sso`), removed on 2026-08-06. That endpoint converts an
 * existing *portal* session into a viewer session; with no portal cookie it
 * redirects straight back to the viewer — which is to say, back to this screen.
 * Anyone reading this screen is by definition not signed in, so the link could
 * only ever loop, under a label promising passwordless entry.
 *
 * The hop itself is untouched and still works where it belongs: a top-level
 * navigation from the portal, which carries the SameSite=Lax portal cookie.
 * Arriving that way, the user never sees this screen at all.
 */
export function renderLoginScreen(
  container: HTMLElement,
  onSuccess: () => void,
  notice = '',
): void {
  container.innerHTML = `
    <div class="screen screen--login">
      <form class="card login-card" novalidate>
        <h1 class="brand">GeoViewer</h1>
        <p class="brand-sub">DigiGeoData web map</p>
        ${notice ? `<p class="login-notice" role="status">${escapeHtml(notice)}</p>` : ''}
        <label>Email
          <input type="email" name="email" autocomplete="username" required autofocus />
        </label>
        <label>Password
          <input type="password" name="password" autocomplete="current-password" required />
        </label>
        <p class="login-links">
          <a href="${FORGOT_PASSWORD_URL}" target="_blank" rel="noopener">Forgot your password?</a>
        </p>
        <p class="login-error" role="alert" hidden></p>
        <button type="submit" class="btn btn--primary">Sign in</button>
      </form>
    </div>`

  const form = container.querySelector('form') as HTMLFormElement
  const errorEl = form.querySelector('.login-error') as HTMLElement
  const submitBtn = form.querySelector('button[type=submit]') as HTMLButtonElement

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    const data = new FormData(form)
    const email = String(data.get('email') ?? '').trim()
    const password = String(data.get('password') ?? '')
    if (!email || !password) return

    errorEl.hidden = true
    submitBtn.disabled = true
    submitBtn.textContent = 'Signing in…'
    try {
      await login(email, password)
      onSuccess()
    } catch (err) {
      errorEl.textContent =
        err instanceof ApiError && err.kind === 'auth'
          ? 'Invalid email or password'
          : 'Sign-in is temporarily unavailable. Please try again.'
      errorEl.hidden = false
      submitBtn.disabled = false
      submitBtn.textContent = 'Sign in'
    }
  })
}
